import { isIOS } from 'components/Option';
import { googleMapsUrl } from './formatCoordinates';

const NUM = `-?\\d{1,3}(?:\\.\\d+)?(?!\\d)`;
const MIN = `\\s*\\d{1,2}(?:\\.\\d+)?(?!\\d)`;
const PART = `${NUM}°?(?:${MIN}'?)?(?:${MIN}"?)?`;
const COMP = `(?:[NSEW]\\s*${PART}|${PART}(?:\\s*[NSEW])?)(?![\\w'"°])`;
const PAIR = `(?:^|[^\\w.'"°])(${COMP})\\s*[,;/]?\\s*(${COMP})`;
const LABELED = new RegExp(
  `\\blat(?:itude)?\\s*[:=]?\\s*(${NUM})\\D*?\\b(?:lng|lon(?:g(?:itude)?)?)\\s*[:=]?\\s*(${NUM})`,
  'i'
);
const PART_RE = /^([NSEW]?)\s*(-?)([\d.]+)°?(?:\s*([\d.]+)'?)?(?:\s*([\d.]+)"?)?\s*([NSEW]?)$/;
const DECIMAL = /^-?\d+\.\d+$/;
const BARE_INT = /^[NSEW]?\s*-?\d+\s*[NSEW]?$/;

const decode = (text) => {
  try {
    return decodeURIComponent(text);
  } catch {
    return text;
  }
};

const inRange = (latitude, longitude) =>
  Math.abs(latitude) <= 90 && Math.abs(longitude) <= 180 ? { latitude, longitude } : null;

const parsePart = (part) => {
  const [, lead, sign, deg, min = 0, sec = 0, trail] = part.match(PART_RE);
  if (min >= 60 || sec >= 60) return null;
  const hemi = lead || trail;
  const negative = sign || /[SW]/.test(hemi);
  return {
    axis: hemi && (/[NS]/.test(hemi) ? 'lat' : 'lng'),
    value: (negative ? -1 : 1) * (+deg + min / 60 + sec / 3600),
  };
};

const toCoords = (a, b) => {
  const plain = !/[°NSEW]/.test(a + b);
  // Letters alone don't make an integer a coordinate: "50 N 100 W" is a grid street address.
  if ([a, b].some((part) => (plain ? !DECIMAL.test(part) : BARE_INT.test(part)))) return null;
  let [lat, lng] = [a, b].map(parsePart);
  if (!lat || !lng || (lat.axis && lat.axis === lng.axis)) return null;
  if (lat.axis === 'lng' || lng.axis === 'lat') [lat, lng] = [lng, lat];
  return inRange(lat.value, lng.value);
};

const parseCoordinates = (address) => {
  if (!address) return null;
  const text = decode(address)
    .replace(/[′’]/g, "'")
    .replace(/''|[″”]/g, '"')
    .replace(/º/g, '°');

  const labeled = text.match(LABELED);
  const coords = labeled && inRange(+labeled[1], +labeled[2]);
  if (coords) return coords;

  const pairs = new RegExp(PAIR, 'g');
  // Retry from the next char so a rejected pair can't swallow the first half of a valid one.
  for (let m; (m = pairs.exec(text)); pairs.lastIndex = m.index + 1) {
    const pair = toCoords(m[1], m[2]);
    if (pair) return pair;
  }
  return null;
};

const navParams = ({ latitude, longitude }, name) =>
  `lat=${latitude}&lng=${longitude}&name=${encodeURIComponent(name)}`;

const samsaraUrl = (coords, name) => `samsaradriver://navigation?${navParams(coords, name)}`;

// Every iOS browser carries "Safari/" in its UA, so the others are excluded by their own token.
const isSafari =
  isIOS &&
  !navigator.standalone &&
  /Safari\//.test(navigator.userAgent) &&
  !/CriOS|FxiOS|EdgiOS|OPT\/|GSA\/|DuckDuckGo/.test(navigator.userAgent);

export const isMobileDevice = isIOS || /Android/i.test(navigator.userAgent);

export const openLocation = ({ name, address }, useSamsara, showNotice) => {
  const coords = parseCoordinates(address);
  const googleUrl = coords
    ? googleMapsUrl(coords)
    : `https://www.google.com/maps/place/${encodeURIComponent(address || '')}`;

  if (!useSamsara || !coords) {
    window.open(googleUrl, '_blank', 'noreferrer');
    return;
  }

  if (!isIOS) {
    const fallback = encodeURIComponent(googleUrl);
    window.location.href = `intent://navigation?${navParams(coords, name || address)}#Intent;scheme=samsaradriver;S.browser_fallback_url=${fallback};end`;
    return;
  }

  // iOS can't detect the app: only a hidden page means Samsara opened; its alert just blurs.
  let timer;
  const handlers = {
    blur: () => clearTimeout(timer),
    focus: () => {
      if (isSafari) showNotice(true);
      wait(1100);
    },
    pagehide: () => cleanup(),
    visibilitychange: () => document.hidden && cleanup(),
  };
  const listen = (method) =>
    Object.entries(handlers).forEach(([type, fn]) => window[method](type, fn));
  const cleanup = () => {
    clearTimeout(timer);
    listen('removeEventListener');
    showNotice(false);
  };
  const wait = (ms) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      cleanup();
      window.location.href = googleUrl;
    }, ms);
  };

  listen('addEventListener');
  window.location.href = samsaraUrl(coords, name || address);
  wait(1500);
};
